import axios from "axios";
import axiosInstance, { BASE_URL } from "@/lib/axios";
import { getCookie } from "@/lib/cookies";
import { serverFetch } from "@/lib/serverFetch";

import type {
  ApiErrorResponse,
  FeaturedResponse,
  HomeBlockDetailParams,
  HomeBlockDetailRequest,
  HomeBlockDetailResponse,
  HomeBlocksResponse,
  HomeResponse,
} from "@/types/home";

const FEATURED_URL = "/featured";
const HOME_BLOCKS_URL = "/home/blocks";
const HOME_URL = "/home";
const HOME_SLUG_URL = "/pages";

function optionalAuthHeaders(): Record<string, string> {
  const token = getCookie("access_token");
  return token ? { Authorization: token } : {};
}

export async function getFeatured(): Promise<FeaturedResponse> {
  try {
    const { data } = await axiosInstance.get<FeaturedResponse>(FEATURED_URL, {
      withCredentials: true,
    });
    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError?.(err)) {
      const apiError = err.response?.data as ApiErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function getHomeBlocks(): Promise<HomeBlocksResponse> {
  try {
    const { data } = await axiosInstance.get<HomeBlocksResponse>(HOME_BLOCKS_URL, {
      withCredentials: true,
    });
    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError?.(err)) {
      const apiError = err.response?.data as ApiErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function getHomeBlockDetail(
  request: HomeBlockDetailRequest,
): Promise<HomeBlockDetailResponse> {
  const { slug, params } = request;
  try {
    const { data } = await axiosInstance.get<HomeBlockDetailResponse>(`${HOME_BLOCKS_URL}/${slug}`, {
      params,
      withCredentials: true,
    });
    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError?.(err)) {
      const apiError = err.response?.data as ApiErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function getHome(params?: HomeBlockDetailParams): Promise<HomeResponse> {
  try {
    const { data } = await axiosInstance.get<HomeResponse>(HOME_URL, {
      params,
      withCredentials: true,
    });
    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError?.(err)) {
      const apiError = err.response?.data as ApiErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function getHomeSlug(
  request: HomeBlockDetailRequest,
): Promise<HomeResponse> {
  const { slug, params } = request;
  try {
    const { data } = await axios.get<HomeResponse>(
      `${BASE_URL}${HOME_SLUG_URL}/${slug}`,
      {
        params,
        headers: optionalAuthHeaders(),
        withCredentials: true,
      },
    );
    return data;
  } catch (err: unknown) {
    if (axios.isAxiosError?.(err)) {
      const apiError = err.response?.data as ApiErrorResponse;
      throw apiError;
    }
    throw err;
  }
}

export async function getHomeBlockDetailCached(
  request: HomeBlockDetailRequest,
): Promise<HomeBlockDetailResponse> {
  const { slug, params } = request;
  return serverFetch<HomeBlockDetailResponse>(`${HOME_BLOCKS_URL}/${slug}`, {
    revalidate: 300,
    tags: ["home-blocks"],
    params: params as Record<string, string | number | boolean | null | undefined>,
  });
}

export async function getHomeBlocksCached(): Promise<HomeBlocksResponse> {
  return serverFetch<HomeBlocksResponse>(HOME_BLOCKS_URL, {
    revalidate: 300,
    tags: ["home-blocks"],
  });
}

export async function getHomeCached(
  params?: HomeBlockDetailParams,
): Promise<HomeResponse> {
  return serverFetch<HomeResponse>(HOME_URL, {
    revalidate: 300,
    tags: ["home"],
    params: params as Record<string, string | number | boolean | null | undefined>,
  });
}

export async function getHomeSlugCached(
  request: HomeBlockDetailRequest,
): Promise<HomeResponse> {
  const { slug, params } = request;
  return serverFetch<HomeResponse>(`${HOME_SLUG_URL}/${slug}`, {
    revalidate: 300,
    tags: ["home-slug", `home-slug-${slug}`],
    params: params as Record<string, string | number | boolean | null | undefined>,
  });
}